import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  getDocumentById,
  loadMarkdownContent,
  parseFrontmatter,
  prepareDocumentationMarkdown,
  userCanAccessDocument,
} from '../../app/helpers/documentationWikiHelper';
import { isDocumentPdfExportEnabled, getDocumentationPdfExportStrategy } from '../../app/helpers/documentationWikiConfigHelper';
import { exportDocumentationToPdf } from '../../app/helpers/documentationPdfExportHelper';
import DocHeaderCard from './components/DocHeaderCard';
import DocsMarkdownRenderer from './components/DocsMarkdownRenderer';

const DocumentationViewer = ({ docId }) => {
  const { t } = useTranslation();
  const { roleLogin } = useSelector((state) => state.userRole);
  const configurationState = useSelector((state) => state.configuration);
  const printAreaRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportError, setExportError] = useState(null);

  const manifestDoc = useMemo(() => getDocumentById(docId), [docId]);

  const viewModel = useMemo(() => {
    if (!manifestDoc) {
      return { error: 'notFound' };
    }

    if (manifestDoc.type === 'externalLink') {
      return { error: 'external', manifestDoc };
    }

    if (!userCanAccessDocument(manifestDoc, roleLogin, configurationState)) {
      return { error: 'forbidden' };
    }

    const raw = loadMarkdownContent(manifestDoc.file);
    if (!raw) {
      return { error: 'missingFile' };
    }

    const { frontmatter, body } = parseFrontmatter(raw);
    const sections = prepareDocumentationMarkdown(body, frontmatter.title);
    return { manifestDoc, frontmatter, sections };
  }, [configurationState, manifestDoc, roleLogin]);

  useEffect(() => {
    if (viewModel.error !== 'external' || !viewModel.manifestDoc?.externalUrl) return;
    window.open(
      viewModel.manifestDoc.externalUrl,
      viewModel.manifestDoc.openInNewTab ? '_blank' : '_self',
    );
  }, [viewModel]);

  const handleExportPdf = async () => {
    if (exportingPdf || viewModel.error) return;

    const { manifestDoc, frontmatter, sections } = viewModel;
    if (!manifestDoc?.file || !sections) return;

    const strategy = getDocumentationPdfExportStrategy(configurationState) || 'backend';

    if (strategy === 'frontend' && !printAreaRef.current) return;

    setExportError(null);
    setExportingPdf(true);

    try {
      const raw = loadMarkdownContent(manifestDoc.file);
      const parsed = raw ? parseFrontmatter(raw) : { frontmatter: {}, body: '' };
      const markdownBody = parsed.body || sections.join('\n\n<!-- docs-page-break -->\n\n');
      const title = frontmatter?.title || parsed.frontmatter?.title || manifestDoc.id || docId;

      await exportDocumentationToPdf({
        strategy,
        sourceElement: printAreaRef.current,
        docId,
        title,
        markdown: markdownBody,
        frontmatter: frontmatter || parsed.frontmatter || {},
      });
    } catch (error) {
      console.error('[documentation] PDF export failed', error);
      setExportError(t('docs.exportPdfError'));
    } finally {
      setExportingPdf(false);
    }
  };

  if (viewModel.error === 'notFound') {
    return <Alert variant="danger">{t('docs.errors.notFound')}</Alert>;
  }

  if (viewModel.error === 'forbidden') {
    return <Alert variant="warning">{t('docs.errors.forbidden')}</Alert>;
  }

  if (viewModel.error === 'missingFile') {
    return <Alert variant="warning">{t('docs.errors.missingFile')}</Alert>;
  }

  if (viewModel.error === 'external') {
    return <Alert variant="info">{t('docs.externalOpened')}</Alert>;
  }

  const { frontmatter, sections, manifestDoc: docMeta } = viewModel;
  const showPdfButton = isDocumentPdfExportEnabled(docMeta, configurationState);

  return (
    <article ref={printAreaRef} id="doc-print-area" className="docs-viewer-article">
      <DocHeaderCard
        frontmatter={frontmatter}
        manifestDoc={docMeta}
        showPdfButton={showPdfButton}
        exportingPdf={exportingPdf}
        onExportPdf={handleExportPdf}
      />

      {exportError && (
        <Alert variant="danger" className="docs-no-print">{exportError}</Alert>
      )}

      <DocsMarkdownRenderer sections={sections} docId={docId} />
    </article>
  );
};

export default DocumentationViewer;
